import { createSlice, createEntityAdapter, nanoid, PayloadAction } from '@reduxjs/toolkit';

export type MessageLevel = 'success' | 'error' | 'info' | 'warning';

export interface MessageAction {
  label: string;
  href?: string;
  onClick?: () => void;
}

export interface Message {
  id: string;
  message: string;
  subtitle?: string;
  level: MessageLevel;
  scope: string;
  ttlMs?: number;
  createdAt: number;
  actions?: MessageAction[];
  close?: () => void;
}

interface AddMessagePayload {
  id?: string;
  message: string;
  subtitle?: string;
  level?: MessageLevel;
  ttlMs?: number;
  scope?: string;
  actions?: MessageAction[];
  close?: () => void;
}

interface MessageExtraState {
  defaultTtlMs: number;
}

// Simple state interface for selectors
interface SimpleMessageState {
  messages: {
    ids: string[];
    entities: Record<string, Message>;
    defaultTtlMs: number;
  };
}

const messagesAdapter = createEntityAdapter<Message>({
  sortComparer: (a, b) => a.createdAt - b.createdAt,
});

const initialState = messagesAdapter.getInitialState<MessageExtraState>({
  defaultTtlMs: 6000, // MessageBars typically stay longer than toasts
});

const messageSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    addMessage: {
      prepare: (payload: AddMessagePayload) => {
        const id = payload.id ?? nanoid();
        return {
          payload: {
            id,
            message: payload.message,
            subtitle: payload.subtitle,
            level: payload.level ?? 'info',
            scope: payload.scope ?? 'global',
            ttlMs: payload.ttlMs ?? initialState.defaultTtlMs,
            createdAt: Date.now(),
            actions: payload.actions,
            close: payload.close,
          } as Message,
        };
      },
      reducer: (state, action: PayloadAction<Message>) => {
        messagesAdapter.addOne(state, action.payload);
      },
    },
    removeMessage: (state, action: PayloadAction<string>) => {
      messagesAdapter.removeOne(state, action.payload);
    },
    clearMessages: (state) => {
      messagesAdapter.removeAll(state);
    },
    setDefaultTtl: (state, action: PayloadAction<number>) => {
      state.defaultTtlMs = action.payload;
    },
  },
});

export const { addMessage, removeMessage, clearMessages, setDefaultTtl } = messageSlice.actions;

export default messageSlice.reducer;

// Selectors
export const selectAllMessages = (state: SimpleMessageState) => {
  const messageState = state.messages;
  if (!messageState) return [];
  return Object.values(messageState.entities || {}) as Message[];
};

export const selectMessageById = (state: SimpleMessageState, id: string) => {
  const messageState = state.messages;
  if (!messageState || !messageState.entities) return undefined;
  return messageState.entities[id] as Message | undefined;
};

export const selectMessagesByScope = (scope: string) => (state: SimpleMessageState) => {
  const allMessages = selectAllMessages(state);
  return allMessages.filter((message) => message.scope === scope);
};

export const selectMessagesByLevel = (level: MessageLevel) => (state: SimpleMessageState) => {
  const allMessages = selectAllMessages(state);
  return allMessages.filter((message) => message.level === level);
};
